import { useEffect } from "react";
import type { ReactNode } from "react";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import useTranslation from "next-translate/useTranslation";
import { get } from "lodash";
import { useFormikContext } from "formik";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import type { DatePickerProps as MuiDatePickerProps } from "@mui/x-date-pickers/DatePicker";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import type { TextFieldProps } from "@mui/material/TextField";
import { DATE_MASK, DATE_TIME_FORMAT } from "@/utils/constants/common";
import { useFormContext } from "@/hooks/useFormContext";
import { FormSkeleton } from "../../skeleton";

type FormValues = Record<string, unknown>;

export interface DatePickerProps {
  id?: string;
  name: string;
  helperText?: ReactNode;
  label?: ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  value?: unknown;
  inputFormat?: string;
  required?: boolean;
  disablePast?: boolean;
  clearable?: boolean;
  minDate?: unknown;
  maxDate?: unknown;
  tooltip?: object;
  loading?: boolean;
  variant?: TextFieldProps["variant"];
  placeholder?: string;
  onChange?: (value: Dayjs | null) => void;
  error?: string;
}

export function DatePicker({
  id,
  name,
  helperText,
  label,
  disabled,
  readOnly,
  value,
  inputFormat = DATE_MASK,
  required,
  disablePast,
  clearable,
  minDate,
  maxDate,
  tooltip,
  loading,
  variant = "outlined",
  placeholder,
  onChange,
  error,
}: DatePickerProps) {
  const { lang } = useTranslation();
  const formik = useFormikContext<FormValues>();
  const formContext = useFormContext();
  const fieldProps = {
    id,
    disabled,
    inputFormat,
    disablePast,
    allowSameDateSelection: true,
    clearable,
    minDate,
    maxDate,
  };

  useEffect(() => {
    formik.setFieldError(name, error);
  }, [error]);

  const handleChange = (newValue: Dayjs | null) => {
    if (dayjs(newValue, DATE_MASK, true).isValid()) {
      formik.setFieldValue(name, dayjs(newValue).format(DATE_TIME_FORMAT));
    } else {
      formik.setFieldValue(name, null);
    }
  };

  return (
    <FormSkeleton loading={loading ?? formContext?.loading}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={lang}>
        <FormControl sx={{ width: "100%" }}>
          <MuiDatePicker
            {...(fieldProps as unknown as Partial<MuiDatePickerProps<Dayjs, Dayjs>>)}
            value={(value ?? get(formik.values, name, null)) as Dayjs | null}
            onChange={onChange ?? handleChange}
            renderInput={(params) => (
              <TextField
                {...({
                  ...params,
                  label,
                  variant,
                  required,
                  readOnly,
                  tooltip,
                  error: get(formik?.touched, name) && Boolean(get(formik.errors, name)),
                  helperText: get(formik?.touched, name) ? get(formik.errors, name) : helperText,
                  inputProps: {
                    ...params.inputProps,
                    placeholder,
                  },
                } as unknown as TextFieldProps)}
              />
            )}
          />
        </FormControl>
      </LocalizationProvider>
    </FormSkeleton>
  );
}
